import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { fetchJson } from "../lib/api";
import { apiBase } from "../lib/apiBase";
import { useAuth } from "../lib/AuthContext";
import Tooltip from "./ui/tooltip";

const FLAG_KEYS = [
    {
        key: "signal_engine",
        aliases: ["dream_engine"],
        label: "Dream Engine",
        helper: "When paused, dream/signal generation stops and no new trades are opened.",
        tooltip: "Pausing this engine will keep signal generation halted until you manually resume it.",
    },
    {
        key: "scalping_engine",
        aliases: [],
        label: "Scalping Engine",
        helper: "When paused, the scalping executor stays halted and does not open positions.",
        tooltip: "Pausing this engine will keep the scalping executor halted until you resume it.",
    },
    {
        key: "horizon_engine",
        aliases: ["nebula_horizon"],
        label: "Horizon Engine",
        helper: "When paused, Horizon will not manage or rebalance positions.",
        tooltip: "Pausing this engine will keep Horizon offline until you manually resume it.",
    },
];

const ALL_FLAG_KEYS = FLAG_KEYS.flatMap(({ key, aliases = [] }) => [key, ...aliases]);

function normalizeFlags(data = {}) {
    const source = data?.module_flags || data?.flags || data || {};
    const normalized = ALL_FLAG_KEYS.reduce(
        (acc, key) => ({
            ...acc,
            [key]: Boolean(source?.[key]),
        }),
        {},
    );

    FLAG_KEYS.forEach(({ key, aliases = [] }) => {
        normalized[key] = Boolean(
            normalized[key] || aliases.some((alias) => normalized[alias]),
        );
    });

    return normalized;
}

export default function SafetyControls({ className = "" }) {
    const { logout } = useAuth();
    const [heartbeat, setHeartbeat] = useState(null);
    const [flags, setFlags] = useState(() => normalizeFlags());
    const [status, setStatus] = useState(() => normalizeFlags());
    const [flagsAvailable, setFlagsAvailable] = useState(false);
    const [statusAvailable, setStatusAvailable] = useState(false);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [error, setError] = useState("");
    const [sessionExpired, setSessionExpired] = useState(false);
    const [authRequired, setAuthRequired] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [pinModalOpen, setPinModalOpen] = useState(false);
    const [pinValue, setPinValue] = useState("");
    const [pinError, setPinError] = useState("");
    // Start collapsed by default so the dashboard loads in a consistent, compact state.
    const configuredPin = useMemo(() => {
        const runtimeEnv = (() => {
            if (typeof globalThis !== "undefined" && globalThis.__IMPORT_META_ENV__) {
                return globalThis.__IMPORT_META_ENV__;
            }
            if (typeof import.meta !== "undefined" && import.meta?.env) {
                return import.meta.env;
            }
            return {};
        })();
        const envPin = (runtimeEnv?.VITE_SAFETY_CONFIRM_PIN ?? "").trim();
        if (envPin) return envPin;

        const legacyPin = (typeof globalThis !== "undefined" && globalThis.__SAFETY_CONFIRM_PIN__) || "";
        return String(legacyPin || "").trim();
    }, []);
    const pinRequired = configuredPin.length > 0;

    const resetAvailabilityState = useCallback(() => {
        setFlagsAvailable(false);
        setStatusAvailable(false);
        setFlags(normalizeFlags());
        setStatus(normalizeFlags());
        setHeartbeat(null);
    }, []);

    const handleSessionExpired = useCallback(() => {
        setSessionExpired(true);
        setAuthRequired(true);
        resetAvailabilityState();
        setError("Session expired. Please sign in again.");
    }, [resetAvailabilityState]);

    const handleAccessDenied = useCallback(() => {
        setSessionExpired(false);
        setAuthRequired(true);
        resetAvailabilityState();
        setError("Not authorized to access safety controls.");
    }, [resetAvailabilityState]);

    const shouldExpireSession = useCallback((res) => Boolean(res?.unauth && res?.httpStatus === 401), []);
    const shouldDenyAccess = useCallback(
        (res) => Boolean(res?.unauth && res?.httpStatus && res?.httpStatus !== 401),
        [],
    );

    const failedUpdateMessage = "Failed to update safety flags.";

    const loadData = useCallback(
        async (options = {}) => {
            const { silent = false } = options;
            if (!silent) {
                setLoading(true);
            }
            setFlagsAvailable(false);
            setStatusAvailable(false);
            setAuthRequired(false);
            setError("");
            try {
                const [hbRes, flagRes, statusRes] = await Promise.all([
                    fetchJson(apiBase("heartbeat")),
                    fetchJson(apiBase("shutdown_flags")),
                    fetchJson(apiBase("shutdown_status")),
                ]);
                if ([hbRes, flagRes, statusRes].some(shouldExpireSession)) {
                    handleSessionExpired();
                    return;
                }
                if ([hbRes, flagRes, statusRes].some(shouldDenyAccess)) {
                    handleAccessDenied();
                    return;
                }
                const hadError = !hbRes.ok || !flagRes.ok || !statusRes.ok;
                if (hadError) {
                    setError("Unable to load safety controls.");
                }
                setFlagsAvailable(flagRes.ok);
                setStatusAvailable(statusRes.ok);
                setSessionExpired(false);
                setAuthRequired(false);
                setHeartbeat(hbRes.data || null);
                setFlags(normalizeFlags(flagRes.data));
                setStatus(normalizeFlags(statusRes.data));
            } catch {
                setError("Unable to load safety controls.");
                resetAvailabilityState();
                setAuthRequired(false);
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [handleAccessDenied, handleSessionExpired, resetAvailabilityState, shouldDenyAccess, shouldExpireSession],
    );

    useEffect(() => {
        loadData();
    }, [loadData]);

    const lastTimestamp = useMemo(() => {
        if (!heartbeat) return null;
        const candidate = heartbeat?.last?.ts ?? heartbeat?.ts ?? heartbeat?.last?.timestamp;
        if (!candidate) return null;
        const millis = candidate > 10 ** 12 ? candidate : candidate * 1000;
        return new Date(millis);
    }, [heartbeat]);

    const applySafetyChange = useCallback(
        async ({ key, nextValue }) => {
            const payload = { [key]: nextValue };
            setUpdating(true);
            setError("");
            try {
                const res = await fetchJson(apiBase("shutdown_flags"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
                if (shouldExpireSession(res)) {
                    handleSessionExpired();
                    return;
                }
                if (shouldDenyAccess(res)) {
                    handleAccessDenied();
                    return;
                }
                if (!res.ok) {
                    setError(failedUpdateMessage);
                    return;
                }
                setSessionExpired(false);
                setFlags((prev) => ({ ...prev, ...payload }));
                await loadData();
            } catch {
                setError(failedUpdateMessage);
            } finally {
                setUpdating(false);
            }
        },
        [failedUpdateMessage, handleAccessDenied, handleSessionExpired, loadData, shouldDenyAccess, shouldExpireSession],
    );

    const hasAnyActiveFlags = useMemo(() => Object.values(flags).some(Boolean), [flags]);
    const hasAnyActiveStatuses = useMemo(() => Object.values(status).some(Boolean), [status]);
    const hasHeartbeatIssue = useMemo(() => {
        if (!heartbeat) return false;
        if (heartbeat.status && heartbeat.status !== "ok") {
            return true;
        }
        if (typeof heartbeat.reachable === "boolean" && !heartbeat.reachable) {
            return true;
        }
        return false;
    }, [heartbeat]);

    const showStatusIndicator = hasAnyActiveFlags || hasAnyActiveStatuses || hasHeartbeatIssue || Boolean(error);

    const resetPinModal = useCallback(() => {
        setPinModalOpen(false);
        setPendingAction(null);
        setPinValue("");
        setPinError("");
    }, []);

    const startPinFlow = useCallback(
        (key) => {
            if (pinModalOpen || updating) return;
            const action = { key, nextValue: !flags[key] };
            if (!pinRequired) {
                applySafetyChange(action);
                return;
            }
            setPendingAction(action);
            setPinValue("");
            setPinError("");
            setPinModalOpen(true);
        },
        [applySafetyChange, flags, pinRequired, pinModalOpen, updating],
    );

    const validatePin = useCallback(
        (inputPin) => {
            if (!pinRequired) return { valid: true };
            const normalized = String(inputPin ?? "").trim();
            if (!normalized) {
                return { valid: false, error: "PIN is required." };
            }
            if (!/^\d+$/.test(normalized)) {
                return { valid: false, error: "PIN must contain digits only." };
            }
            if (configuredPin.length && normalized.length !== configuredPin.length) {
                return {
                    valid: false,
                    error: `PIN must be a ${configuredPin.length}-digit code.`,
                };
            }
            if (normalized !== configuredPin) {
                return { valid: false, error: "Incorrect PIN. Try again." };
            }
            return { valid: true };
        },
        [configuredPin, pinRequired],
    );

    const handleConfirmPin = useCallback(() => {
        if (!pendingAction) return;
        const result = validatePin(pinValue);
        if (!result.valid) {
            setPinError(result.error || "Incorrect PIN. Please try again.");
            return;
        }

        const action = pendingAction;
        resetPinModal();
        applySafetyChange(action);
    }, [applySafetyChange, pendingAction, pinValue, resetPinModal, validatePin]);

    const handleCancelPin = useCallback(() => {
        resetPinModal();
    }, [resetPinModal]);

    useEffect(() => {
        if (!pinModalOpen) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                handleCancelPin();
            }
            if (event.key === "Enter") {
                event.preventDefault();
                handleConfirmPin();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleCancelPin, handleConfirmPin, pinModalOpen]);

    const indicator = (
        <span
            className={`inline-flex h-2.5 w-2.5 rounded-full ${
                showStatusIndicator ? "bg-red-400 animate-pulse" : "bg-emerald-400/70"
            }`}
            aria-label={showStatusIndicator ? "Safety controls require attention" : "Safety controls healthy"}
            data-testid="safety-controls-indicator"
        />
    );

    const loadingSkeleton = (
        <div className="space-y-4 animate-pulse" data-testid="safety-controls-loading">
            <div className="h-4 bg-gray-800 rounded w-56" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {FLAG_KEYS.map(({ key }) => (
                    <div
                        key={key}
                        className="bg-gray-950/60 border border-gray-800 rounded-xl px-4 py-3 flex flex-col gap-2"
                    >
                        <div className="h-4 bg-gray-800 rounded w-32" />
                        <div className="h-3 bg-gray-800 rounded w-20" />
                    </div>
                ))}
            </div>
            <div className="h-3 bg-gray-800 rounded w-28" />
        </div>
    );

    return (
        <>
            <div
                className={`space-y-4 border border-gray-800 rounded-2xl bg-gray-900/70 p-4 ${className}`}
                data-testid="safety-controls"
            >
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-[0.65rem] uppercase tracking-wide text-gray-400">Heartbeat & shutdown management</p>
                        <h3 className="text-lg font-semibold text-white">Safety Controls</h3>
                    </div>
                    {indicator}
                </div>
                <div className="text-xs text-gray-400 flex items-center justify-between">
                    <p>
                        Safety locks pause each engine until you enter a PIN to resume them. The status chips below show whether an engine is currently running or paused.
                    </p>
                    {pinRequired && (
                        <span className="text-[11px] text-purple-200" data-testid="pin-required-indicator">
                            PIN required
                        </span>
                    )}
                </div>
                {error && (
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-red-300 bg-red-900/30 border border-red-500/40 rounded p-2" data-testid="safety-error">
                        <span>{error}</span>
                        {sessionExpired && (
                            <button
                                type="button"
                                className="text-xs font-semibold text-white bg-red-700/60 border border-red-400/60 rounded px-2 py-1 hover:bg-red-700"
                                onClick={() => logout()}
                            >
                                Sign in again
                            </button>
                        )}
                    </div>
                )}
                <div role="status" aria-live="polite" className="space-y-3">
                    {loading ? (
                        loadingSkeleton
                    ) : (
                        <>
                            <div className="flex items-center justify-between text-sm text-gray-300" data-testid="heartbeat-status">
                                <span>Heartbeat Status:</span>
                                <span className="font-mono text-purple-200">
                                    {lastTimestamp ? lastTimestamp.toLocaleString() : "No recent heartbeat"}
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {FLAG_KEYS.map(({ key, label, helper, tooltip }) => {
                                    const isPaused = Boolean(flags[key]);
                                    const liveActive = Boolean(status[key]);
                                    const statusClass = !flagsAvailable
                                        ? "bg-gray-500/20 text-gray-200 border border-gray-400/60"
                                        : isPaused
                                            ? "bg-amber-500/20 text-amber-100 border border-amber-300/60"
                                            : "bg-emerald-500/20 text-emerald-100 border border-emerald-400/60";
                                    const liveClass = !statusAvailable
                                        ? "text-gray-300"
                                        : liveActive
                                            ? "text-amber-200"
                                            : "text-emerald-200";
                                    const statusText = !flagsAvailable
                                        ? authRequired
                                            ? "Unknown (auth required)"
                                            : "Unknown (flags unavailable)"
                                        : isPaused
                                            ? "Paused (safety lock on)"
                                            : "Running";
                                    const runtimeText = !statusAvailable
                                        ? authRequired
                                            ? "Unknown (auth required)"
                                            : "Unknown (status unavailable)"
                                        : liveActive
                                            ? "Paused in system"
                                            : "Running";
                                    const actionLabel = !flagsAvailable ? "Unavailable" : isPaused ? "Resume Engine" : "Pause Engine";
                                    const actionDisabled = updating || pinModalOpen || !flagsAvailable;

                                    return (
                                        <Tooltip
                                            key={key}
                                            content={(
                                                <div data-testid={`tooltip-content-${key}`} className="space-y-1">
                                                    <p className="font-semibold">{label}</p>
                                                    <p className="text-xs text-gray-100 leading-snug">{tooltip}</p>
                                                </div>
                                            )}
                                        >
                                            <div
                                                className="bg-gray-950/70 border border-gray-800 rounded-xl px-4 py-3 flex flex-col gap-3"
                                                data-testid={`safety-control-${key}`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-semibold text-white">{label}</span>
                                                            <span
                                                                className={`text-[0.7rem] px-2 py-1 rounded-full font-semibold uppercase tracking-wide ${statusClass}`}
                                                                data-testid={`status-chip-${key}`}
                                                            >
                                                                {statusText}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-gray-300 leading-snug">{helper}</p>
                                                        <p
                                                            className={`text-[11px] leading-snug ${liveClass}`}
                                                            data-testid={`live-status-${key}`}
                                                        >
                                                            {runtimeText}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className={`rounded-lg px-3 py-2 text-sm font-semibold border transition-colors ${
                                                            isPaused
                                                                ? "bg-purple-900/40 border-purple-400/60 text-purple-100 hover:bg-purple-900/60"
                                                                : "bg-gray-800/70 border-rose-500/60 text-rose-100 hover:bg-gray-800/90"
                                                        } disabled:opacity-60 disabled:cursor-not-allowed`}
                                                        onClick={() => startPinFlow(key)}
                                                        data-testid={`toggle-${key}`}
                                                        disabled={actionDisabled}
                                                    >
                                                        {actionLabel}
                                                    </button>
                                                </div>
                                            </div>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-400">
                                <button
                                    type="button"
                                    className="underline text-purple-200 disabled:opacity-60"
                                    onClick={() => loadData({ silent: true })}
                                    disabled={loading || updating}
                                >
                                    Refresh Status
                                </button>
                                {updating && <span>Applying changes…</span>}
                            </div>
                        </>
                    )}
                </div>
            </div>
            {pinModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={handleCancelPin}
                    role="dialog"
                    aria-modal="true"
                    data-testid="safety-pin-modal"
                >
                    <div
                        className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-md"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                            <h3 className="text-lg font-semibold text-white">Confirm safety change</h3>
                            <button
                                type="button"
                                className="text-gray-400 hover:text-white"
                                onClick={handleCancelPin}
                                aria-label="Close safety confirmation"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="px-4 py-3 space-y-3 text-sm text-gray-100">
                            {(() => {
                                const target = FLAG_KEYS.find((f) => f.key === pendingAction?.key);
                                const actionText = pendingAction
                                    ? pendingAction.nextValue
                                        ? "pause"
                                        : "resume"
                                    : "update";
                                return (
                                    <p className="leading-snug">
                                        {pendingAction
                                            ? `You’re about to ${actionText} the ${target?.label || "engine"}.`
                                            : "You’re about to change an engine state."}
                                    </p>
                                );
                            })()}
                            <p className="text-xs text-gray-400">
                                Enter your PIN to confirm. This prevents accidental pauses or resumes.
                            </p>
                            <div className="space-y-1">
                                <label htmlFor="safety-pin-input" className="text-xs text-gray-300 uppercase tracking-wide">
                                    PIN
                                </label>
                                <input
                                    id="safety-pin-input"
                                    type="password"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    className="w-full rounded-md bg-gray-950 border border-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    value={pinValue}
                                    onChange={(e) => {
                                        setPinValue(e.target.value || "");
                                        if (pinError) setPinError("");
                                    }}
                                    autoFocus
                                    data-testid="pin-input"
                                />
                                {pinError && (
                                    <div className="text-xs text-rose-200" data-testid="pin-error">
                                        {pinError}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-800 bg-gray-950/50 rounded-b-xl">
                            <button
                                type="button"
                                className="px-3 py-2 text-sm rounded-md border border-gray-700 text-gray-200 hover:bg-gray-800"
                                onClick={handleCancelPin}
                                data-testid="pin-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="px-3 py-2 text-sm rounded-md bg-purple-700 text-white font-semibold hover:bg-purple-600 disabled:opacity-60"
                                onClick={handleConfirmPin}
                                disabled={updating}
                                data-testid="pin-confirm"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

SafetyControls.propTypes = {
    className: PropTypes.string,
};
